// Named ranges (Fixes 9): a word that stands for a block of cells, =SUM(Rates) for =SUM(B2:B10), on any tab, as in
// Google Sheets. Each is kept on the tab its cells are on (its data's names: name to range, "B2:B10"), so it goes
// to the trash and back with its tab, moves when rows or columns are put in or taken out there, and keeps pointing at
// the same cells when the tab is renamed. A name is the sheet's: two tabs never hold the same one.

import { parseRect } from "./addr";
import { shiftFormula } from "./shift";

export const MAX_NAMES = 100;
const NAME = /^[A-Za-z_][A-Za-z0-9_.]{0,99}$/;

export interface NamedRange {
  name: string;
  tabId: string;
  tab: string;
  /** a block of the tab, "B2:B10", "B:B" or "2:4"; "#REF!" once all of its cells were deleted */
  range: string;
}

/** Why a word cannot name a range, in plain words, or null when it can. */
export function nameProblem(name: string): string | null {
  if (!name.trim()) return "Give the range a name, such as Rates.";
  if (!NAME.test(name)) return `“${name.slice(0, 40)}” can't be a name: start with a letter, then letters, digits or _ (no spaces), up to 100 characters.`;
  if (/^[A-Za-z]{1,3}[0-9]+$/.test(name) || /^R[0-9]*C[0-9]*$/i.test(name)) return `“${name}” looks like a cell's address. Pick a word, such as Rates.`;
  if (/^(TRUE|FALSE)$/i.test(name)) return `“${name}” is a value. Pick a word, such as Rates.`;
  return null;
}

/** A range as kept: capitals, no $, a block parseRect reads; null when it is not one. */
export function cleanRange(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const s = raw.trim().replace(/\$/g, "").toUpperCase();
  if (s === "#REF!") return s;
  return /^[A-Z0-9:]{1,20}$/.test(s) && parseRect(s) ? s : null;
}

/** A tab's named ranges, each name and range sound (cleanSheetTab); undefined for none. */
export function cleanNames(raw: unknown): Record<string, string> | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (Object.keys(out).length >= MAX_NAMES) break;
    const range = cleanRange(v);
    if (!range || nameProblem(k)) continue;
    // one spelling for a name: the first given
    if (Object.keys(out).some((x) => x.toLowerCase() === k.toLowerCase())) continue;
    out[k] = range;
  }
  return Object.keys(out).length ? out : undefined;
}

/** Every named range of a sheet, in its tabs' order. */
export function sheetNames(tabs: { entry: { id: string }; data: { name: string; names?: Record<string, string> } }[]): NamedRange[] {
  const out: NamedRange[] = [];
  const seen = new Set<string>();
  for (const t of tabs)
    for (const [name, range] of Object.entries(t.data.names ?? {})) {
      const k = name.toLowerCase();
      if (seen.has(k)) continue;
      seen.add(k);
      out.push({ name, tabId: t.entry.id, tab: t.data.name, range });
    }
  return out;
}

/** The named range a word names, any case. */
export function findName(names: NamedRange[], word: string): NamedRange | null {
  const k = word.trim().toLowerCase();
  return names.find((n) => n.name.toLowerCase() === k) ?? null;
}

/** A tab's named ranges once rows or columns were put in or taken out there: as formulas' references move. */
export function shiftNames(names: Record<string, string>, tabName: string, l: { axis: "row" | "col"; at: number; n: number; del: boolean }): Record<string, string> | null {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(names)) out[k] = v === "#REF!" ? v : shiftFormula(`=${v}`, tabName, { tab: tabName, ...l }).slice(1);
  return Object.keys(out).length ? out : null;
}
